import { useTranslation } from "react-i18next"
import { flexRender, type Table as ReactTable } from "@tanstack/react-table"
import { cva, type VariantProps } from "class-variance-authority"
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react"

import Icon from "@/components/Icon"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { cn } from "@/helpers/classNames"

import { Button } from "./button"

const tableVariants = cva([], {
  variants: {
    size: {
      default: "px-4 py-2",
      sm: `
        px-0
        first:px-4
        last:px-4
      `,
    },
  },
  defaultVariants: {
    size: "default",
  },
})

interface StatelessDataTableProps<T> extends VariantProps<
  typeof tableVariants
> {
  emptyStateMessage?: string
  getCellClassName?: (row: T) => string
  isLoading?: boolean
  table: ReactTable<T>
}

export function StatelessDataTable<T>({
  emptyStateMessage,
  getCellClassName,
  isLoading,
  size,
  table,
}: StatelessDataTableProps<T>) {
  const { t } = useTranslation()

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header, index) => {
                const isFirst = index === 0
                const isLast = index === headerGroup.headers.length - 1

                return (
                  <TableHead
                    key={header.id}
                    className={cn(
                      tableVariants({ size }),
                      isFirst &&
                        `
                          sticky left-0 z-20
                          shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]
                        `,
                      isLast &&
                        `
                          sticky right-0 z-20
                          shadow-[-2px_0_5px_-2px_rgba(0,0,0,0.1)]
                        `,
                    )}
                    style={{ width: header.column.columnDef.size }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                )
              })}
            </TableRow>
          ))}
        </TableHeader>
        {!isLoading && (
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                >
                  {row.getVisibleCells().map((cell, index) => {
                    const isFirst = index === 0
                    const isLast = index === row.getVisibleCells().length - 1

                    return (
                      <TableCell
                        key={cell.id}
                        className={cn(
                          tableVariants({ size }),
                          isFirst &&
                            `
                              sticky left-0 z-20
                              shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]
                            `,
                          isLast &&
                            `
                              sticky right-0 z-20
                              shadow-[-2px_0_5px_-2px_rgba(0,0,0,0.1)]
                            `,
                          getCellClassName
                            ? getCellClassName(row.original)
                            : undefined,
                        )}
                        style={{ width: cell.column.columnDef.size }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    )
                  })}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={table.getAllColumns().length}
                  className="h-24 text-center"
                >
                  <div className="font-body-xlarge-bold text-primary-200">
                    {emptyStateMessage
                      ? emptyStateMessage
                      : t("global.emptySubtitle")}
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        )}
      </Table>
      {isLoading && <LoadingState />}
      {!isLoading && table.getRowCount() > 0 && (
        <DataTablePaginationBar table={table} />
      )}
    </div>
  )
}

function LoadingState() {
  return (
    <div className="flex h-96 w-full items-center justify-center bg-neutral-100">
      <Icon name="spinner" size="lg" />
    </div>
  )
}

function DataTablePaginationBar<T>({ table }: { table: ReactTable<T> }) {
  const page = table.getState().pagination.pageIndex + 1
  const pageSize = table.getState().pagination.pageSize
  const total = table.getRowCount()
  const start = (page - 1) * pageSize + 1
  const end = Math.min(page * pageSize, total)

  return (
    <div
      className="
        flex h-10 items-center justify-end gap-1 border border-neutral-300
        bg-neutral-100 px-2
      "
    >
      {start} - {end} / {total}
      <Button
        onClick={table.previousPage}
        disabled={!table.getCanPreviousPage()}
        variant="ghost"
        size="icon"
        className="size-8"
      >
        <ChevronLeftIcon />
      </Button>
      <Button
        onClick={table.nextPage}
        disabled={!table.getCanNextPage()}
        variant="ghost"
        size="icon"
        className="size-8"
      >
        <ChevronRightIcon />
      </Button>
    </div>
  )
}
